import styled, { createGlobalStyle } from 'styled-components';

/* Phones and small tablets: one scrolling column — team grid, then settings,
   with Enter Draft in a fixed bar. Ends at 785px because the team cards
   switch to their desktop shape at 786px; below that the two columns didn't
   fit and the page scrolled sideways. */
const MOBILE = '@media only screen and (max-width: 785px)';

/* Height of the fixed Enter Draft bar plus a little air, kept clear at
   the bottom of the page so the last settings row can scroll above it. */
const MOBILE_BAR_SPACE = 'calc(96px + env(safe-area-inset-bottom, 0px))';

/* ─── Page shell ─────────────────────────────────────────────────── */

export const PageWrap = styled.section`
  /* Padding, not margin: as the page's last element its bottom MARGIN would
     collapse through the parent and vanish, leaving the card flush with the
     bottom of the screen while the top kept its 20px. Padding never collapses,
     so both ends stay even. */
  padding: 20px 0;

  ${MOBILE} {
    padding: 0;
  }
`;

export const Container = styled.div`
  margin: 0 auto;
  max-width: 1400px;

  /* Large desktops / 4K: the card sat at 1400px in a 2560px viewport, leaving
     most of the screen empty. The widths below are chosen so the team grid
     still lands 8 cards per row as everything scales up. */
  @media only screen and (min-width: 1700px) {
    max-width: min(1900px, calc(100vw - 80px));
  }

  @media only screen and (min-width: 2100px) {
    max-width: min(2200px, calc(100vw - 120px));
  }

  @media only screen and (min-width: 3000px) {
    max-width: clamp(2800px, 90vw, 4000px);
  }
`;

export const TwoColLayout = styled.div`
  display: flex;
  background: #ffffff;
  border-radius: 12px;
  gap: 20px;

  ${MOBILE} {
    flex-direction: column;
    gap: 0;
    border-radius: 0;
    padding-bottom: ${MOBILE_BAR_SPACE};
  }
`;

export const LeftCol = styled.div`
  flex: 1;
  min-width: 0;
  padding-bottom: clamp(20px, 1.1vw, 48px);

  /* The team grid comes first on mobile; it's second in the markup. */
  ${MOBILE} {
    order: -1;
    padding: 0 16px 20px;
  }
`;

export const RightCol = styled.div`
  width: 400px;
  flex-shrink: 0;
  border-right: 1px solid rgb(229, 231, 235);

  /* TwoColLayout stretches this column to the team grid's height, so laying
     it out as a column lets Enter Draft sit on the bottom edge via
     margin-top:auto — the panel then self-balances at any grid height
     instead of needing its spacing re-tuned whenever the cards resize. */
  display: flex;
  flex-direction: column;

  /* MUI renders Switch, Checkbox and Radio at a fixed pixel size, so they stayed
     small while everything around them scaled. Scale the switch as a whole
     and drive the checkbox and radio through their icons' font-size. */
  @media only screen and (min-width: 1700px) {
    .MuiSwitch-root {
      transform: scale(1.2);
      margin: 4px 6px;
    }
    .MuiCheckbox-root .MuiSvgIcon-root,
    .MuiRadio-root .MuiSvgIcon-root { font-size: 1.9rem; }
  }

  @media only screen and (min-width: 2100px) {
    .MuiSwitch-root {
      transform: scale(1.35);
      margin: 7px 10px;
    }
    .MuiCheckbox-root .MuiSvgIcon-root,
    .MuiRadio-root .MuiSvgIcon-root { font-size: 2.2rem; }
  }

  @media only screen and (min-width: 3000px) {
    .MuiSwitch-root {
      transform: scale(1.9);
      margin: 17px 26px;
    }
    .MuiCheckbox-root .MuiSvgIcon-root,
    .MuiRadio-root .MuiSvgIcon-root { font-size: 3.4rem; }
  }

  /* Fluid rather than two steps: the type inside grew ~1.8x on large screens
     while the column only went 400 -> 460px, so the controls ran out of room. */
  @media only screen and (min-width: 1700px) {
    width: clamp(420px, 21vw, 820px);
  }

  ${MOBILE} {
    width: auto;
    border-right: none;
    border-top: 1px solid rgb(229, 231, 235);
  }
`;

/* ─── Config page header ─────────────────────────────────────────── */

export const ConfigHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: clamp(16px, 1vw, 38px) 18px clamp(12px, 0.75vw, 29px);

  ${MOBILE} {
    padding: 20px 16px 12px;
  }
`;

export const TeamsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px 12px 0;

  ${MOBILE} {
    flex-wrap: wrap;
    gap: 8px;
    padding: 16px 0 12px;
  }
`;

/* Right side of the teams header: total count over the Select All toggle.
   On mobile they share one row under the title. */
export const TeamsHeaderRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: clamp(2px, 0.15vw, 8px);
  flex-shrink: 0;

  ${MOBILE} {
    width: 100%;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;

    /* Keeps Select All on the right while the count is still loading. */
    & > :last-child { margin-left: auto; }
  }
`;

export const DraftTotalCount = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(18px, 1.13vw, 43px);
  font-weight: 700;
  color: #012337;
  white-space: nowrap;

  ${MOBILE} {
    font-size: 15px;
  }
`;

export const ConfigTitle = styled.h1`
  font-size: clamp(26px, 1.63vw, 62px);
  font-weight: 700;
  color: #0e1118;
  margin: 0;
`;

export const ConfigSubtitle = styled.p`
  font-size: clamp(14px, 0.88vw, 34px);
  font-weight: 400;
  color: #6b7280;
  margin: 2px 0 0;
`;

export const GearBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: 1px solid #c8cdd2;
  border-radius: 8px;
  padding: 6px 12px;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(13px, 0.82vw, 31px);
  font-weight: 600;
  color: #3e464f;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: #0098cd;
    background: #f0f9ff;
    color: #0e1118;
  }

  svg {
    width: clamp(15px, 0.94vw, 36px);
    height: clamp(15px, 0.94vw, 36px);
  }
`;

/* ─── Advanced settings modal body ───────────────────────────────── */

export const SettingsBody = styled.div`
  padding: 4px clamp(20px, 1.25vw, 48px) clamp(24px, 1.5vw, 58px);
  display: flex;
  flex-direction: column;
  gap: clamp(20px, 1.25vw, 48px);
`;

export const SettingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 4px 0;
`;

export const SettingLabelWrap = styled.div``;

export const SettingLabel = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(14px, 0.88vw, 34px);
  font-weight: 600;
  color: #0e1118;
`;

export const SettingDesc = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(12px, 0.75vw, 29px);
  color: #6b7280;
  margin-top: 1px;
`;

export const SettingSliderWrap = styled.div`
  flex: 1;
  max-width: clamp(180px, 13vw, 500px);

  /* MUI draws the slider at a fixed size, so scale its parts explicitly. */
  .MuiSlider-thumb {
    width: clamp(20px, 1.2vw, 48px);
    height: clamp(20px, 1.2vw, 48px);
  }
  .MuiSlider-rail,
  .MuiSlider-track { height: clamp(4px, 0.28vw, 11px); }
`;

export const SettingMarksRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(13px, 0.82vw, 31px);
  font-weight: 600;
  color: #9ca3af;
  margin-top: 2px;
`;

export const SettingCheckRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: clamp(14px, 0.9vw, 34px);
`;

export const SettingCheckLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 7px;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(14px, 0.88vw, 34px);
  font-weight: 600;
  color: #0e1118;
  cursor: pointer;

  input[type='checkbox'] {
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: #004ea3;
  }
`;

export const SettingDivider = styled.hr`
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 0;
  width: 100%;

  ${MOBILE} {
    order: ${({ $mobileOrder }) => $mobileOrder ?? 0};
  }
`;

export const AdvancedSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(10px, 0.55vw, 21px);
`;

export const AdvancedSummaryWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const AdvancedSummaryHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

export const AdvancedSummaryTitle = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(15px, 0.94vw, 36px);
  font-weight: 700;
  color: #0e1118;
`;

export const AdvancedSummarySubtitle = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(12px, 0.75vw, 29px);
  color: #6b7280;
  margin-top: 2px;
`;

export const EditSettingsOutlineBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border: 1px solid rgb(210, 210, 210);
  border-radius: 8px;
  background: #fff;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(13px, 0.82vw, 31px);
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover { 
    border-color: rgb(180, 180, 180);
    background: rgb(245, 245, 245);
  }
`;

export const AdvancedSummaryCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  position: relative;
`;

export const AdvancedSummaryRow = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(12px, 0.75vw, 29px);
  padding: clamp(6px, 0.6vw, 24px) 14px;
  border-bottom: 1px solid #e5e7eb;
  position: relative;
  &:first-child { border-radius: 10px 10px 0 0; }
  &:last-child { border-bottom: none; border-radius: 0 0 10px 10px; }
`;

// A draft mode row: the label for its radio. Hover and selected look like the Speed and Rounds
// buttons, background only.
export const DraftModeRow = styled(AdvancedSummaryRow)`
  cursor: pointer;
  transition: background 0.2s;
  &.active { background: #e3f2fd; }
  &:hover:not(.active) { background: rgb(245, 245, 245); }

  /* Tighter so the Single Mock Draft row (title, Options, radio) fits a 320px screen. */
  ${MOBILE} {
    gap: 8px;
    padding: 6px 8px 6px 12px;
  }
`;

// The icon alone, no circle behind it. The wrap is sized to the icon so every row's text starts
// at the same x.
export const AdvancedSummaryIconWrap = styled.div`
  width: clamp(24px, 1.5vw, 58px);
  height: clamp(24px, 1.5vw, 58px);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #16a34a;

  svg {
    width: clamp(24px, 1.5vw, 58px);
    height: clamp(24px, 1.5vw, 58px);
  }
`;

export const AdvancedSummaryRowContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const AdvancedSummaryRowTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(14px, 0.88vw, 34px);
  font-weight: 700;
  color: #0e1118;
  white-space: nowrap;

  svg {
    width: clamp(14px, 0.88vw, 34px);
    height: clamp(14px, 0.88vw, 34px);
  }
`;

export const AdvancedSummaryRowValue = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(13px, 0.82vw, 31px);
  font-weight: 600;
  color: #16a34a;
`;

export const SummaryBar = styled.button`
  display: flex;
  width: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  padding: 0;
  overflow: hidden;
  &:hover { border-color: #d1d5db; background: #fafafa; }
`;

export const SummaryBarItem = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px 8px;
  gap: 3px;
  border-right: 1px solid #e5e7eb;
  &:last-child { border-right: none; }
`;

export const SummaryBarLabel = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(12px, 0.75vw, 29px);
  font-weight: 600;
  color: #374151;
`;

export const SummaryBarValue = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(13px, 0.82vw, 31px);
  font-weight: 700;
  color: #16a34a;
`;


// Opens the advanced settings dialog from the Single Mock Draft row. Same footprint as the
// Intensity badge on the Fanatic Mode row, in blue.
export const DraftModeOptionsBtn = styled.button`
  display: flex;
  align-items: center;
  padding: 6px 6px 6px 12px;
  border: 1px solid #2563eb;
  border-radius: 20px;
  background: #eff6ff;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(14px, 0.88vw, 34px);
  font-weight: 600;
  color: #1d4ed8;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;
  &:hover { background: #dbeafe; }
  &:focus-visible { outline: 2px solid #1d4ed8; outline-offset: 2px; }

  svg {
    width: clamp(20px, 1.25vw, 48px);
    height: clamp(20px, 1.25vw, 48px);
  }

  ${MOBILE} {
    padding: 4px 2px 4px 10px;
  }
`;

export const IntensityWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

export const IntensityBadge = styled.button`
  display: flex;
  align-items: center;
  padding: 6px 6px 6px 12px;
  border: 1px solid #ea580c;
  border-radius: 20px;
  background: #fff7ed;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(14px, 0.88vw, 34px);
  font-weight: 600;
  color: #ea580c;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;
  &:hover { background: #ffedd5; }

  svg {
    width: clamp(14px, 0.88vw, 34px);
    height: clamp(14px, 0.88vw, 34px);
  }
`;

export const IntensityPopover = styled.div`
  position: relative;
  width: clamp(240px, 15vw, 560px);
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
  padding: clamp(14px, 0.88vw, 34px) clamp(22px, 1.4vw, 53px);

  &::after {
    content: '';
    position: absolute;
    bottom: -6px;
    right: 16px;
    width: 10px;
    height: 10px;
    background: #fff;
    border-right: 1px solid #e5e7eb;
    border-bottom: 1px solid #e5e7eb;
    transform: rotate(45deg);
  }
`;

export const IntensityPopoverTitle = styled.div`
  font-size: clamp(14px, 0.88vw, 34px);
  font-weight: 700;
  color: #0e1118;
  margin-bottom: 8px;
`;

export const AdvancedModalBody = styled.div`
  display: flex;
  flex-direction: column;
  padding: 4px clamp(20px, 1.25vw, 48px) clamp(20px, 1.25vw, 48px);
`;

export const ResetDefaultsRow = styled.div`
  display: flex;
  justify-content: center;
  padding-top: clamp(14px, 0.88vw, 34px);
`;

export const ResetDefaultsBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: clamp(8px, 0.5vw, 19px);
  padding: clamp(7px, 0.45vw, 17px) clamp(18px, 1.1vw, 42px);
  background: #ffcdd2;
  border: none;
  border-radius: clamp(8px, 0.5vw, 19px);
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(14px, 0.88vw, 34px);
  font-weight: 600;
  color: #b71c1c;
  cursor: pointer;
  transition: background 0.15s;

  &:hover { background: #ffbcc3; }
  &:focus-visible { outline: 2px solid #b71c1c; outline-offset: 2px; }

  svg {
    width: clamp(18px, 1.1vw, 42px);
    height: clamp(18px, 1.1vw, 42px);
    flex-shrink: 0;
  }
`;

export const ToggleWithLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(14px, 0.88vw, 34px);
  font-weight: 600;
  color: #0e1118;
  white-space: nowrap;
`;

/* ─── Controls card ──────────────────────────────────────────────── */

export const ConfigCard = styled.div`
  padding: 0px 20px 0;

  ${MOBILE} {
    padding: 0 16px;
  }
`;

export const ConfigCardDivider = styled.hr`
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: clamp(20px, 1.25vw, 48px) 0 0;
`;

/* ─── Controls row (Speed + Rounds) ──────────────────────────────── */

export const ControlsRow = styled.div`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: clamp(12px, 0.75vw, 29px);
`;

// $mobileOrder reorders the blocks on mobile only (ControlsRow is a flex row).
export const ControlBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(6px, 0.38vw, 14px);
  width: 100%;

  ${MOBILE} {
    order: ${({ $mobileOrder }) => $mobileOrder ?? 0};
  }
`;

export const ControlLabel = styled.p`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-weight: 600;
  font-size: clamp(16px, 1vw, 38px);
  color: #0e1118;
  margin: 0;
`;

export const AdvancedSubtitle = styled.p`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(12px, 0.75vw, 29px);
  color: #6b7280;
  margin: 2px 0 0;
`;

export const AdvancedHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(4px, 0.3vw, 12px);
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(13px, 0.82vw, 31px);
  font-weight: 600;
  color: #0e1118;
  white-space: nowrap;
  opacity: ${({ $disabled }) => ($disabled ? 0.45 : 1)};
`;

export const OtherOptionsRow = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  background: none;
  border: none;
  padding: clamp(12px, 0.75vw, 29px) 16px;
  cursor: pointer;
  gap: 12px;
  text-align: left;

  &:hover { background: #f9fafb; }

  & > svg {
    width: clamp(24px, 1.5vw, 58px);
    height: clamp(24px, 1.5vw, 58px);
  }
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

export const SelectBtn = styled.button`
  flex: 1;
  height: clamp(40px, 2.5vw, 96px);
  border: 1px solid rgb(210, 210, 210);
  border-radius: 6px;
  background: #fff;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(14px, 0.88vw, 34px);
  font-weight: 600;
  color: #3e464f;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s, background 0.2s;

  &.active {
    border-color: #1976d2;
    background-color: #e3f2fd;
  }

  &:hover:not(.active) {
    border-color: rgb(180, 180, 180);
    background: rgb(245, 245, 245);
  }
`;

export const RoundBtn = styled.button`
  flex: 1;
  height: clamp(44px, 2.75vw, 106px);
  border: 1px solid rgb(210, 210, 210);
  border-radius: 6px;
  background: #fff;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(14px, 0.88vw, 34px);
  font-weight: 600;
  color: #3e464f;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s, background 0.2s;

  &.active {
    border-color: #1976d2;
    background-color: #e3f2fd;
  }

  &:hover:not(.active) {
    border-color: rgb(180, 180, 180);
    background: rgb(245, 245, 245);
  }
`;

export const LoadDraftBtnWrapper = styled.div`
  margin-top: auto;
  padding: clamp(20px, 1.1vw, 48px);
  /* Same value as LeftCol so the gap under Enter Draft matches the
     gap under the last card row. */
  padding-bottom: clamp(20px, 1.1vw, 48px);
  display: flex;
  justify-content: space-around;

  /* Always in reach while the page scrolls. TwoColLayout keeps
     MOBILE_BAR_SPACE free at the bottom so nothing ends up hidden under it.
     z-index sits under ModalShell and the Intensity popper (both 200). */
  ${MOBILE} {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 100;
    margin-top: 0;
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
    background: #fff;
    border-top: 1px solid #e5e7eb;
    box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.08);
  }
`;

export const LoadDraftBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: clamp(14px, 0.78vw, 34px) 32px;
  width: 100%;
  background: linear-gradient(180deg, #4ade80 0%, #16a34a 100%);
  border: 2px solid #86efac;
  border-radius: 12px;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(17px, 1.06vw, 41px);
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  letter-spacing: 0.5px;
  transition: background 0.2s, opacity 0.2s;

  &:hover:not(:disabled) {
    background: linear-gradient(180deg, #22c55e 0%, #15803d 100%);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  svg {
    width: clamp(24px, 1.5vw, 58px);
    height: clamp(24px, 1.5vw, 58px);
  }
`;

export const TeamSectionTitle = styled.h2`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-weight: 700;
  font-size: clamp(24px, 1.5vw, 58px);
  color: #0e1118;
  margin-bottom: 4px;
  padding-left: 18px;
`;

/* ─── Teams loader ───────────────────────────────────────────────── */

export const TeamsLoaderWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 64px 0;
`;

export const TeamsSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 5px solid #ffffffff;
  border-top-color: #004ea3;
  border-radius: 50%;
  animation: spin 0.75s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

/* ─── Teams grid ─────────────────────────────────────────────────── */

export const TeamsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding-right: clamp(20px, 1.1vw, 48px);
  gap: 10px;

  /* Large screens get real columns instead of wrapping flex items: the card
     width then falls out of the column, so the container can widen without
     any risk of the 32 teams reflowing to 7 or 9 per row. */
  @media only screen and (min-width: 1700px) {
    display: grid;
    /* minmax(0, ...) not plain 1fr: a bare 1fr is minmax(auto, 1fr), so a
       column holding a long name (Buccaneers, Commanders) refuses to shrink
       below its min-content width and grows wider than its siblings. */
    grid-template-columns: repeat(8, minmax(0, 1fr));
  }

  /* minmax(0, 1fr) for the same reason as above. */
  ${MOBILE} {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
    padding-right: 0;
  }
`;

/* ─── ESPN card — .team-squares ──────────────────────────────────── */

export const TeamSquares = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;

  /* Selection indicator. An inset shadow rather than a border: a border shrinks
     the padding box, which is the containing block for the absolutely
     positioned .bg-image watermark — so a selected card's logo shifted by the
     border width while every other card's stayed put. A shadow costs no
     layout, and still follows the card's border-radius. */
  &.selected .team-square-bg {
    box-shadow: inset 0 0 0 clamp(4px, 0.25vw, 10px) #0d830f;
  }

  @media only screen and (min-width: 393px) {
    width: 393px;
  }

  @media only screen and (min-width: 786px) {
    width: auto;
  }

  /* Card height fills the screen below the banner. The 4 rows share whatever
     is left after --banner-h plus 230px of chrome:
       20  PageWrap padding-top
       102 section header (padding + title + subtitle)
       18  TeamsGrid padding-bottom
       20  LeftCol padding-bottom
       20  PageWrap padding-bottom
       30  three 10px row gaps
     = 300 exactly, so 320 leaves ~20px of headroom rather than landing on the
     line and tipping into a scrollbar. The clamp floor keeps the previous
     stepped size on short screens, so nothing can shrink. */
  @media only screen and (min-width: 1700px) {
    --card-h: clamp(156px, calc((100vh - var(--banner-h, 90px) - 230px) / 4), 200px);
  }

  @media only screen and (min-width: 2100px) {
    --card-h: clamp(182px, calc((100vh - var(--banner-h, 90px) - 230px) / 4), 250px);
  }

  @media only screen and (min-width: 3000px) {
    --card-h: clamp(182px, calc((100vh - var(--banner-h, 90px) - 230px) / 4), 420px);
  }

  /* Mobile tiles reuse the --card-h ratios of the large-screen cards. The
     height follows the tile's width, so tiles stay about square:
     (100vw - 2 * 16px gutter - 3 * 8px gaps) / 4 columns. */
  ${MOBILE} {
    width: auto;
    --card-h: clamp(72px, calc((100vw - 56px) / 4), 110px);

    &.selected .team-square-bg {
      box-shadow: inset 0 0 0 3px #0d830f;
    }
  }
`;

/* ESPN .show-check — absolute circle, hidden by default */
export const ShowCheck = styled.div`
  background-color: #0d830f;
  border-radius: 50%;
  height: 32px;
  position: absolute;
  right: 9px;
  top: 3px;
  width: 32px;
  z-index: 1;
  display: none;
  align-items: center;
  justify-content: center;

  &.visible {
    display: flex;
  }

  img {
    width: 16px;
    height: 16px;
  }

  @media only screen and (min-width: 786px) {
    right: -8px;
    top: 0;
  }

  /* Same idea — the badge scales with the card so it stays proportional. */
  @media only screen and (min-width: 1700px) {
    width: calc(var(--card-h) * 0.2);
    height: calc(var(--card-h) * 0.2);

    img {
      width: calc(var(--card-h) * 0.1);
      height: calc(var(--card-h) * 0.1);
    }
  }

  /* Inside the tile's corner: the desktop badge hangs off the card's edge,
     which here would land on the next tile. */
  ${MOBILE} {
    top: 4px;
    right: 4px;
    width: calc(var(--card-h) * 0.22);
    height: calc(var(--card-h) * 0.22);

    img {
      width: calc(var(--card-h) * 0.11);
      height: calc(var(--card-h) * 0.11);
    }
  }
`;

/* ESPN .team-square-bg — background layer (team color + gradient + big logo) */
export const TeamSquareBg = styled.div`
  align-items: center;
  /* team color comes from inline style; gradient is a second layer on top */
  background-image: linear-gradient(90deg, hsla(0, 0%, 85%, 0), transparent 0.01%, rgba(0, 0, 0, 0.6));
  border-radius: clamp(10px, 0.63vw, 24px);
  cursor: pointer;
  display: flex;
  height: 56px;
  justify-content: space-between;
  margin: 0;
  overflow: hidden;
  padding: 8px 16px;
  position: relative;
  text-align: end;

  .bg-image {
    bottom: 0;
    left: -100px;
    opacity: 0.1;
    padding: 20px 18.586px 20px 20px;
    position: absolute;
    width: 240px;
  }

  @media only screen and (min-width: 786px) {
    background-image: linear-gradient(180deg, hsla(0, 0%, 85%, 0), transparent 0.01%, rgba(0, 0, 0, 0.6));
    flex-direction: column;
    height: 136px;
    justify-content: space-between;
    margin: 0;
    padding: 0 12px;
    text-align: center;
    width: 135.5px;

    .bg-image {
      bottom: 0;
      left: -100px;
      padding: 20px 6px;
      top: 0;
      width: 200px;
    }
  }

  @media only screen and (min-width: 814px) {
    width: 110px;
  }

  /* The watermark is centred on the card's LEFT EDGE, so its left offset must
     stay at half its own width — otherwise it drifts right and the logo's
     wordmark tail bleeds into the card. */
  /* The watermark tracks the card instead of stepping: at the base size it is
     200px wide on a 136px-tall card, centred on the card's left edge. Holding
     those ratios (1.47 and -0.735) keeps it looking the same at every size —
     the old fixed 240/280px made it shrink relative to a growing card. */
  @media only screen and (min-width: 1700px) {
    width: auto;
    height: var(--card-h);

    .bg-image {
      width: calc(var(--card-h) * 1.47);
      left: calc(var(--card-h) * -0.735);
      padding: calc(var(--card-h) * 0.147) calc(var(--card-h) * 0.044);
    }
  }

  ${MOBILE} {
    background-image: linear-gradient(180deg, hsla(0, 0%, 85%, 0), transparent 0.01%, rgba(0, 0, 0, 0.6));
    flex-direction: column;
    width: auto;
    height: var(--card-h);
    padding: 0 6px;

    .bg-image {
      top: 0;
      width: calc(var(--card-h) * 1.47);
      left: calc(var(--card-h) * -0.735);
      padding: calc(var(--card-h) * 0.147) calc(var(--card-h) * 0.044);
    }
  }
`;

/* ESPN .team-square — content overlay (uses negative margin to sit on top of bg) */
export const TeamSquare = styled.div`
  font-family: ui-sans-serif, system-ui, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji;
  align-items: center;
  border-radius: clamp(10px, 0.63vw, 24px);
  cursor: pointer;
  display: flex;
  height: 56px;
  justify-content: space-between;
  margin: -56px 18px 0;
  overflow: hidden;
  padding: 8px 16px;
  position: relative;
  text-align: end;

  @media only screen and (min-width: 786px) {
    flex-direction: column;
    height: 136px;
    justify-content: space-between;
    margin: -136px 0 0 0;
    padding: 0 12px;
    text-align: center;
    width: 135.5px;
  }

  @media only screen and (min-width: 814px) {
    width: 110px;
  }

  /* The overlay sits on the bg via a negative margin equal to its own height,
     so both read the same --card-h variable set on TeamSquares. */
  @media only screen and (min-width: 1700px) {
    width: auto;
    height: var(--card-h);
    margin: calc(-1 * var(--card-h)) 0 0 0;
  }

  ${MOBILE} {
    flex-direction: column;
    width: auto;
    height: var(--card-h);
    margin: calc(-1 * var(--card-h)) 0 0 0;
    padding: 0 6px;
    text-align: center;
  }
`;

/* Inner logo + name row — .flex.items-center.md:flex-col.md:justify-center.md:mt-[13px] */
export const TeamLogoRow = styled.div`
  display: flex;
  align-items: center;

  img {
    width: 40px;
    height: 40px;
    object-fit: contain;
  }

  @media only screen and (min-width: 786px) {
    flex-direction: column;
    justify-content: center;
    margin-top: 13px;

    img {
      margin-bottom: 4px;
      margin-top: -4px;
    }
  }

  /* Sized off the card itself so the logo tracks --card-h instead of needing
     its own breakpoints. 0.35 runs a little larger than the small cards'
     own ratio (40px on a 136px card = 0.29). */
  @media only screen and (min-width: 1700px) {
    img {
      width: calc(var(--card-h) * 0.35);
      height: calc(var(--card-h) * 0.35);
    }
  }

  ${MOBILE} {
    flex-direction: column;
    justify-content: center;
    margin-top: calc(var(--card-h) * 0.08);

    img {
      width: calc(var(--card-h) * 0.35);
      height: calc(var(--card-h) * 0.35);
      margin-bottom: 2px;
    }
  }
`;

/* Team name — .team-name.text-white.text-base.font-black */
export const TeamName = styled.div`
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  margin-left: 4px;
  font-size: 1rem;
  line-height: 1.5rem;
  color: #fff;
  font-weight: 900;

  @media only screen and (min-width: 786px) {
    margin-left: 0;
    line-height: 1.25;
    margin-top: -4px;
  }

  /* Tied to the card like the logo. 0.11 sits just under the small cards'
     own ratio (16px on a 136px card = 0.118). */
  @media only screen and (min-width: 1700px) {
    font-size: calc(var(--card-h) * 0.11);
  }

  ${MOBILE} {
    display: none;
  }
`;

/* Mobile only: stands in for TeamName, which doesn't fit four to a row. */
export const TeamAbbr = styled.div`
  display: none;
  color: #fff;
  font-weight: 900;
  line-height: 1.1;
  letter-spacing: 0.02em;

  ${MOBILE} {
    display: block;
    font-size: calc(var(--card-h) * 0.15);
  }
`;

export const TeamDivider = styled.div`
  display: block;
  width: 100%;
  height: 1px;
  background: rgba(255, 255, 255, 0.2);
`;

/* Pick number */
export const TeamPick = styled.div`
  font-family: "Saira Semi Condensed", sans-serif
  line-height: normal;
  font-size: 35px;
  color: #fff;
  margin-right: 1rem;
  font-weight: bold;

  @media only screen and (min-width: 786px) {
    margin-right: 0;
    height: 45px;
    margin-bottom: 2px;
  }

  /* Scaled alongside TeamName so the card keeps its internal balance —
     the original design runs the number at ~2.2x the name (35px vs 16px). */
  @media only screen and (min-width: 1700px) {
    font-size: calc(var(--card-h) * 0.28);
    height: calc(var(--card-h) * 0.33);
  }

  ${MOBILE} {
    margin-right: 0;
    margin-bottom: 2px;
    font-size: calc(var(--card-h) * 0.28);
    height: calc(var(--card-h) * 0.33);
  }
`;
